import { useMemo, useRef, useState } from 'react';
import {
  DECOR_PICTURE_DOCUMENT_TYPES,
  DECOR_PICTURE_ENTITY_TYPE,
  DECOR_PICTURE_KINDS,
  DECOR_PICTURE_TYPES,
  MAX_DECOR_PICTURES_PER_KIND,
  MAX_DECOR_PICTURE_BYTES,
  type DecorPalette,
  type DecorPictureKind,
  type EntityAttachment,
} from '@peles/shared';
import { getAttachmentUploadUrl } from '../../../api/entity-attachments';
import { uploadFileToS3 } from '../../../api/photos';
import {
  useCreateEntityAttachment,
  useDeleteEntityAttachment,
  useEntityAttachments,
} from '../../../hooks/useEntityAttachments';
import { loadPixels, paletteFor, type Pixels } from '../../../utils/imagePalette';

/** The record the pictures are filed on — office-only, never the event's own files. */
export const DECOR_ENTITY_TYPE = DECOR_PICTURE_ENTITY_TYPE;

const KIND_BY_TYPE = new Map<string, DecorPictureKind>(
  DECOR_PICTURE_KINDS.map((k) => [DECOR_PICTURE_DOCUMENT_TYPES[k], k]),
);

export type PicturesByKind = Record<DecorPictureKind, EntityAttachment[]>;

/** Why a file cannot be one of the pictures, in words — or null when it can. */
export function pictureProblem(file: File): string | null {
  if (!(DECOR_PICTURE_TYPES as readonly string[]).includes(file.type)) {
    return `${file.name} isn't a JPEG, PNG or WebP picture.`;
  }
  if (file.size > MAX_DECOR_PICTURE_BYTES) {
    return `${file.name} is larger than ${(MAX_DECOR_PICTURE_BYTES / 1_000_000).toFixed(1)} MB. Choose a smaller copy.`;
  }
  return null;
}

/** A picture whose colours could not be read — its group's colours are left as they were. */
export class PictureUnreadableError extends Error {
  constructor() {
    super('A picture could not be read');
  }
}

const NONE: Record<DecorPictureKind, number> = { chairs: 0, flowers: 0, hall: 0, tableware: 0 };

/**
 * The event's decor pictures by group, adding and removing them, and the
 * colours each group gives — read again whenever the group changes and
 * handed to `onPalette`, so the look's palette always describes the pictures
 * that are there.
 *
 * Work on a group goes one change at a time, each reading the group as the
 * change before it left it (not as the last fetch had it), so two quick
 * additions never paint the group from a list missing one of them. Groups do
 * not wait for each other. A colour read that fails saves nothing, and a save
 * that fails stores nothing: either way the group keeps its old colours and is
 * listed in `unread` until `refresh` reads and saves it.
 */
export function useDecorPictures(
  eventId: string,
  onPalette: (kind: DecorPictureKind, palette: DecorPalette) => Promise<void> | void,
) {
  const attachments = useEntityAttachments(DECOR_ENTITY_TYPE, eventId);
  const create = useCreateEntityAttachment();
  const remove = useDeleteEntityAttachment();
  const [pending, setPending] = useState(NONE);
  const [unread, setUnread] = useState<DecorPictureKind[]>([]);
  const [error, setError] = useState<string | null>(null);

  const byKind = useMemo<PicturesByKind>(() => {
    const groups = Object.fromEntries(
      DECOR_PICTURE_KINDS.map((k) => [k, [] as EntityAttachment[]]),
    ) as PicturesByKind;
    (attachments.data ?? []).forEach((a) => {
      const kind = a.documentType ? KIND_BY_TYPE.get(a.documentType) : undefined;
      if (kind) groups[kind].push(a);
    });
    return groups;
  }, [attachments.data]);

  // What this panel has added and taken out that the fetched list may not show yet.
  const server = useRef(byKind);
  server.current = byKind;
  const added = useRef(new Map<string, { kind: DecorPictureKind; picture: EntityAttachment }>());
  const removed = useRef(new Set<string>());
  const files = useRef(new Map<string, File>());
  const chains = useRef<Partial<Record<DecorPictureKind, Promise<void>>>>({});

  /** A group as the changes made here have left it. */
  const current = (kind: DecorPictureKind): EntityAttachment[] => {
    const listed = server.current[kind].filter((p) => !removed.current.has(p.id));
    const ids = new Set(listed.map((p) => p.id));
    const mine = [...added.current.values()]
      .filter(
        (a) => a.kind === kind && !ids.has(a.picture.id) && !removed.current.has(a.picture.id),
      )
      .map((a) => a.picture);
    return [...listed, ...mine];
  };

  /** Runs one change to a group after the one before it, marking the group busy meanwhile. */
  const inTurn = (kind: DecorPictureKind, work: () => Promise<void>): Promise<void> => {
    setPending((p) => ({ ...p, [kind]: p[kind] + 1 }));
    const run = (chains.current[kind] ?? Promise.resolve()).then(work).finally(() => {
      setPending((p) => ({ ...p, [kind]: p[kind] - 1 }));
    });
    chains.current[kind] = run.catch(() => {});
    return run;
  };

  /** Reads a group's colours from what it holds now; refuses to answer for a picture it cannot read. */
  const repaint = async (kind: DecorPictureKind) => {
    const pictures = current(kind);
    const pixels = await Promise.all(
      pictures.map((p) => {
        const source = files.current.get(p.id) ?? p.downloadUrl;
        return source ? loadPixels(source) : Promise.resolve(null);
      }),
    );
    const waiting = () => setUnread((u) => (u.includes(kind) ? u : [...u, kind]));
    if (pixels.some((p) => p === null)) {
      waiting();
      throw new PictureUnreadableError();
    }
    try {
      await onPalette(kind, paletteFor(kind, pixels as Pixels[]));
    } catch (err) {
      // Read but not saved: the stored colours no longer match the pictures,
      // so the group waits for "Read them again" like an unreadable one.
      waiting();
      throw err;
    }
    setUnread((u) => u.filter((k) => k !== kind));
  };

  const unreadMessage =
    'The colours of a picture could not be read, so they were left as they were. Read them again.';

  const add = (kind: DecorPictureKind, files_: File[]) =>
    inTurn(kind, async () => {
      setError(null);
      const room = MAX_DECOR_PICTURES_PER_KIND - current(kind).length;
      if (room <= 0) {
        setError(
          `A group holds ${MAX_DECOR_PICTURES_PER_KIND} pictures. Remove one to add another.`,
        );
        return;
      }
      const problems = files_.map(pictureProblem).filter((p): p is string => p !== null);
      const good = files_.filter((f) => pictureProblem(f) === null).slice(0, room);
      if (problems.length) setError(problems[0]);
      if (good.length === 0) return;
      let uploaded = 0;
      try {
        for (const file of good) {
          const { uploadUrl, s3Key } = await getAttachmentUploadUrl(file.name, file.type);
          await uploadFileToS3(uploadUrl, file);
          const attachment = await create.mutateAsync({
            entityType: DECOR_ENTITY_TYPE,
            entityId: eventId,
            fileName: file.name,
            s3Key,
            mimeType: file.type,
            fileSizeBytes: file.size,
            documentType: DECOR_PICTURE_DOCUMENT_TYPES[kind],
          });
          added.current.set(attachment.id, { kind, picture: attachment });
          files.current.set(attachment.id, file);
          uploaded += 1;
        }
      } catch {
        setError('A picture could not be added. Try again.');
      }
      if (uploaded === 0) return;
      // Whatever did go up is painted in, even when a later file failed.
      try {
        await repaint(kind);
      } catch (err) {
        setError(
          err instanceof PictureUnreadableError
            ? unreadMessage
            : 'The colours could not be saved, so they stay as they were. Read them again.',
        );
      }
    });

  const drop = (kind: DecorPictureKind, picture: EntityAttachment) =>
    inTurn(kind, async () => {
      setError(null);
      try {
        await remove.mutateAsync({
          id: picture.id,
          entityType: DECOR_ENTITY_TYPE,
          entityId: eventId,
        });
      } catch {
        setError('The picture could not be removed. Try again.');
        return;
      }
      removed.current.add(picture.id);
      try {
        await repaint(kind);
      } catch (err) {
        setError(
          err instanceof PictureUnreadableError
            ? unreadMessage
            : 'The colours could not be saved, so they stay as they were. Read them again.',
        );
      }
    });

  /** Reads a group's colours again from the pictures it holds. */
  const refresh = (kind: DecorPictureKind) =>
    inTurn(kind, async () => {
      try {
        await repaint(kind);
      } catch {
        // `unread` says which group is waiting; the colours it has stay.
      }
    });

  return {
    byKind,
    loading: attachments.isLoading,
    /** Whether a group has a change under way. */
    isBusy: (kind: DecorPictureKind) => pending[kind] > 0,
    /** Whether any group has. */
    working: DECOR_PICTURE_KINDS.some((k) => pending[k] > 0),
    unread,
    error,
    add,
    drop,
    refresh,
    total: DECOR_PICTURE_KINDS.reduce((n, k) => n + byKind[k].length, 0),
  };
}
